import {
    AppointmentSessionMatch,
    AppointmentSummaryResponse
} from "../api/controllers/medicalIntegrationConfiguration";

export interface OutboundAppointmentSuccessMetrics {
    denominator: number;
    successfulOutcomes: number;
    rate: number | null;
}

export interface OutboundAppointmentChartMetrics {
    confirmed: number;
    leftMessage: number;
}

export type AppointmentSessionOutcome = 'confirmed' | 'leftMessage';

interface MatchableAppointmentSession {
    sessionId?: string;
    organizationId?: string;
    phoneNumber?: string;
    eventDate?: string;
}

interface TaggedAppointmentEngagement {
    sessionTags?: string[];
    tags?: string[];
}

const toCount = (value: number | undefined): number => {
    return Number.isFinite(value) ? Math.max(Number(value), 0) : 0;
};

const hasCount = (value: number | undefined): boolean => {
    return typeof value === 'number' && Number.isFinite(value);
};

/**
 * Outbound scheduled appointments are defined by OUTBOUND_APPOINTMENT_BOOKED.
 * Count tag occurrences so multi-patient conversations remain accurate.
 */
export const countOutboundScheduledAppointments = (
    records: TaggedAppointmentEngagement[],
    getTagDisplayName?: (tag: string) => string
): number => records.reduce((total, record) => {
    const tags = record.sessionTags || record.tags || [];
    return total + tags.filter(tag =>
        tag === 'OUTBOUND_APPOINTMENT_BOOKED' ||
        getTagDisplayName?.(tag) === 'Outbound Appointment Booked'
    ).length;
}, 0);

export const normalizeAppointmentPhoneNumber = (value: string | undefined): string => {
    const digits = (value || '').replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

const getAppointmentSessionMatchKey = (
    organizationId: string | undefined,
    phoneNumber: string | undefined
): string => `${organizationId || ''}:${normalizeAppointmentPhoneNumber(phoneNumber)}`;

/**
 * Maps Denticon appointment outcomes back to Voicify sessions by organization and
 * normalized phone number. One latest session is selected per matched appointment,
 * preventing retries for the same appointment from inflating the modal totals.
 */
export const getDenticonMatchedAppointmentSessions = <T extends MatchableAppointmentSession>(
    records: T[],
    matches: AppointmentSessionMatch[] | undefined,
    outcome: AppointmentSessionOutcome
): T[] => {
    // An omitted property indicates an older API response. Preserve the original
    // tag-filtered session behavior until the matching contract is available.
    if (matches === undefined) return records;

    const matchCounts = matches.reduce((counts, match) => {
        const includesOutcome = outcome === 'confirmed'
            ? match.confirmedByVoicify
            : match.leftMessageByVoicify;
        const phoneNumber = normalizeAppointmentPhoneNumber(match.phoneNumber);
        if (!includesOutcome || !phoneNumber) return counts;

        const key = getAppointmentSessionMatchKey(match.organizationId, phoneNumber);
        counts.set(key, (counts.get(key) || 0) + 1);
        return counts;
    }, new Map<string, number>());

    const recordsByMatchKey = records.reduce((groups, record) => {
        const phoneNumber = normalizeAppointmentPhoneNumber(record.phoneNumber);
        if (!phoneNumber) return groups;

        const key = getAppointmentSessionMatchKey(record.organizationId, phoneNumber);
        const recordsForKey = groups.get(key) || [];
        recordsForKey.push(record);
        groups.set(key, recordsForKey);
        return groups;
    }, new Map<string, T[]>());

    const selectedRecords = new Set<T>();
    matchCounts.forEach((count, key) => {
        const seenSessionIds = new Set<string>();
        const candidates = (recordsByMatchKey.get(key) || []).filter(record => {
            if (!record.sessionId) return true;
            if (seenSessionIds.has(record.sessionId)) return false;
            seenSessionIds.add(record.sessionId);
            return true;
        });
        candidates
            .slice()
            .sort((left, right) => {
                const leftTime = Date.parse(left.eventDate || '') || 0;
                const rightTime = Date.parse(right.eventDate || '') || 0;
                return rightTime - leftTime;
            })
            .slice(0, count)
            .forEach(record => selectedRecords.add(record));
    });

    return records.filter(record => selectedRecords.has(record));
};

/**
 * Returns the number of Denticon appointments represented by each selected
 * Voicify conversation. A single family call can update more than one
 * appointment, even though analytics contains only one conversation record.
 *
 * When there are more matched appointments than distinct tagged sessions for a
 * phone number, the additional appointments are assigned to the latest session.
 */
export const getDenticonAppointmentCountsBySession = <T extends MatchableAppointmentSession>(
    records: T[],
    matches: AppointmentSessionMatch[] | undefined,
    outcome: AppointmentSessionOutcome
): Map<T, number> => {
    const selectedRecords = getDenticonMatchedAppointmentSessions(records, matches, outcome);
    const countsByRecord = new Map<T, number>();

    if (matches === undefined) {
        selectedRecords.forEach(record => countsByRecord.set(record, 1));
        return countsByRecord;
    }

    const appointmentCountsByKey = matches.reduce((counts, match) => {
        const includesOutcome = outcome === 'confirmed'
            ? match.confirmedByVoicify
            : match.leftMessageByVoicify;
        const phoneNumber = normalizeAppointmentPhoneNumber(match.phoneNumber);
        if (!includesOutcome || !phoneNumber) return counts;

        const key = getAppointmentSessionMatchKey(match.organizationId, phoneNumber);
        counts.set(key, (counts.get(key) || 0) + 1);
        return counts;
    }, new Map<string, number>());

    const selectedByKey = selectedRecords.reduce((groups, record) => {
        const phoneNumber = normalizeAppointmentPhoneNumber(record.phoneNumber);
        if (!phoneNumber) return groups;

        const key = getAppointmentSessionMatchKey(record.organizationId, phoneNumber);
        const recordsForKey = groups.get(key) || [];
        recordsForKey.push(record);
        groups.set(key, recordsForKey);
        return groups;
    }, new Map<string, T[]>());

    selectedByKey.forEach((recordsForKey, key) => {
        const appointmentCount = appointmentCountsByKey.get(key) || 0;
        const sortedRecords = recordsForKey.slice().sort((left, right) => {
            const leftTime = Date.parse(left.eventDate || '') || 0;
            const rightTime = Date.parse(right.eventDate || '') || 0;
            return rightTime - leftTime;
        });

        sortedRecords.forEach((record, index) => {
            const representedAppointments = index === 0
                ? Math.max(appointmentCount - sortedRecords.length + 1, 1)
                : 1;
            countsByRecord.set(record, representedAppointments);
        });
    });

    return countsByRecord;
};

export const getOutboundAppointmentChartMetrics = (
    summary: Partial<AppointmentSummaryResponse> | null | undefined,
    fallback: Partial<OutboundAppointmentChartMetrics> = {}
): OutboundAppointmentChartMetrics => ({
    confirmed: hasCount(summary?.confirmedByVoicify)
        ? toCount(summary?.confirmedByVoicify)
        : toCount(fallback.confirmed),
    leftMessage: hasCount(summary?.leftMessageByVoicify)
        ? toCount(summary?.leftMessageByVoicify)
        : toCount(fallback.leftMessage)
});

/**
 * Calculates the final patient outcome rate for appointments contacted by Voicify.
 *
 * Confirmations after a left message are already included in totalConfirmed, so they
 * must not be added again. missedWithBookingAfterVoicifyCall is also a subset of
 * missedWithSubsequentBooking and must not be added separately. A missed appointment that resulted in a later booking is
 * a recovered outcome and is included in the numerator.
 */
export const calculateOutboundAppointmentSuccess = (
    summary: Partial<AppointmentSummaryResponse> | null | undefined,
    decimalPlaces: number = 0
): OutboundAppointmentSuccessMetrics => {
    if (!summary) {
        return {
            denominator: 0,
            successfulOutcomes: 0,
            rate: null
        };
    }

    const denominator = hasCount(summary.eligibleAppointments)
        ? toCount(summary.eligibleAppointments)
        : Math.max(
            toCount(summary.totalAppointments) - toCount(summary.totalLeftMessage),
            0
        );
    const successfulOutcomes = hasCount(summary.successfulOutcomes)
        ? toCount(summary.successfulOutcomes)
        : toCount(summary.totalConfirmed) +
            toCount(summary.totalCancelled) +
            toCount(summary.totalReschedules) +
            toCount(summary.totalCheckedOut) +
            toCount(summary.missedWithSubsequentBooking);

    if (denominator <= 0) {
        return {
            denominator,
            successfulOutcomes,
            rate: null
        };
    }

    const precision = Math.pow(10, Math.max(decimalPlaces, 0));

    return {
        denominator,
        successfulOutcomes,
        rate: Math.round((successfulOutcomes / denominator) * 100 * precision) / precision
    };
};
